import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Movie } from '@/types/movie';
import { getGenreNames } from '@/utils/movie';

interface HeroCarouselProps {
  movies: Movie[];
}

export default function HeroCarousel({ movies }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const touchStart = useRef<number | null>(null);
  const paused = useRef(false);

  const go = useCallback(
    (next: number) => {
      if (movies.length === 0) return;
      setIndex((next + movies.length) % movies.length);
    },
    [movies.length],
  );

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (!paused.current) setIndex((i) => (i + 1) % movies.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [movies.length]);

  if (movies.length === 0) return null;

  return (
    <section
      className="relative h-[560px] w-full overflow-hidden bg-background-50 md:h-[640px]"
      onMouseEnter={() => {
        paused.current = true;
      }}
      onMouseLeave={() => {
        paused.current = false;
      }}
      onTouchStart={(e) => {
        touchStart.current = e.touches[0].clientX;
      }}
      onTouchEnd={(e) => {
        if (touchStart.current === null) return;
        const delta = e.changedTouches[0].clientX - touchStart.current;
        if (Math.abs(delta) > 50) go(delta < 0 ? index + 1 : index - 1);
        touchStart.current = null;
      }}
      aria-roledescription="carousel"
      aria-label="推荐电影轮播"
    >
      {movies.map((movie, i) => (
        <div
          key={movie.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? 'opacity-100' : 'pointer-events-none opacity-0'
          }`}
          aria-hidden={i !== index}
        >
          <img
            src={movie.backdrop}
            alt={`${movie.title}主视觉`}
            title={`${movie.title} 影片剧照`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/60 to-background-50/30" />
          <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/40 to-transparent" />
        </div>
      ))}

      <div className="relative z-10 flex h-full items-end pb-16 md:items-center md:pb-0">
        <div className="container-page w-full">
          <div className="max-w-2xl">
            {movies.map((movie, i) => (
              <div key={movie.id} className={i === index ? 'block animate-fade-up' : 'hidden'}>
                <span className="inline-flex items-center gap-2 rounded-full border border-primary-500/40 bg-primary-500/15 px-3 py-1 text-xs font-semibold text-primary-300">
                  <i className="ri-fire-fill" aria-hidden="true" />
                  热门推荐
                </span>
                <h2 className="mt-4 font-heading text-4xl font-extrabold leading-tight text-foreground-50 text-shadow-hero md:text-6xl">
                  {movie.title}
                </h2>
                <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-foreground-300">
                  <span className="font-heading font-bold text-primary-400">{movie.rating.toFixed(1)} 分</span>
                  <span className="text-foreground-600">|</span>
                  <span>{movie.year}</span>
                  <span className="text-foreground-600">|</span>
                  <span>{getGenreNames(movie.genres)}</span>
                  <span className="text-foreground-600">|</span>
                  <span>{movie.region}</span>
                </div>
                <p className="mt-4 max-w-xl text-sm leading-relaxed text-foreground-300 md:text-base">
                  {movie.synopsis}
                </p>
                <div className="mt-7 flex flex-wrap items-center gap-3">
                  <Link
                    to={`/detail/${movie.id}`}
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-bold text-foreground-950 transition-colors hover:bg-primary-400"
                  >
                    <i className="ri-play-circle-fill text-lg" aria-hidden="true" />
                    立即观看
                  </Link>
                  <Link
                    to={`/detail/${movie.id}`}
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-foreground-50/30 bg-background-50/40 px-6 py-3 text-sm font-semibold text-foreground-100 backdrop-blur-sm transition-colors hover:border-primary-400 hover:text-primary-300"
                  >
                    影片详情
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2">
        {movies.map((movie, i) => (
          <button
            key={movie.id}
            type="button"
            onClick={() => go(i)}
            aria-label={`切换到第 ${i + 1} 张`}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? 'w-8 bg-primary-500' : 'w-3 bg-foreground-50/40 hover:bg-foreground-50/70'
            }`}
          />
        ))}
      </div>

      <button
        type="button"
        onClick={() => go(index - 1)}
        aria-label="上一张"
        className="absolute left-3 top-1/2 z-20 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-foreground-50/20 bg-background-50/50 text-xl text-foreground-100 backdrop-blur-sm transition-colors hover:bg-primary-500 hover:text-foreground-950 md:flex cursor-pointer"
      >
        <i className="ri-arrow-left-s-line" aria-hidden="true" />
      </button>
      <button
        type="button"
        onClick={() => go(index + 1)}
        aria-label="下一张"
        className="absolute right-3 top-1/2 z-20 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-foreground-50/20 bg-background-50/50 text-xl text-foreground-100 backdrop-blur-sm transition-colors hover:bg-primary-500 hover:text-foreground-950 md:flex cursor-pointer"
      >
        <i className="ri-arrow-right-s-line" aria-hidden="true" />
      </button>
    </section>
  );
}